import dayjs from "dayjs";
import type { I18nDateFormatter, I18nTranslate } from "../types";

// date
export const getLocalizedWeekdayArray = (t: I18nTranslate) =>
  [
    t("date.sunday"),
    t("date.monday"),
    t("date.tuesday"),
    t("date.wednesday"),
    t("date.thursday"),
    t("date.friday"),
    t("date.saturday"),
  ] as const;

export function isInvalidDateOrString(source: string | Date | number | null | undefined) {
  /* random numbers will be converted to dates in JS (e.g. 730 is a date in 1971) */
  if (!source || typeof source === "number") return true;
  if (typeof source === "string") {
    if (source?.length <= 3) return true;
    /* stricter validation for date only strings - dayjs.isValid() works best with ISO */
    if (source?.length === 10)
      return !dayjs(
        source,
        [
          "YYYY-MM-DD",
          "YYYY-DD-MM",
          "DD-MM-YYYY",
          "MM-DD-YYYY",
          "YYYY/MM/DD",
          "YYYY/DD/MM",
          "MM/DD/YYYY",
          "DD/MM/YYYY",
        ],
        true,
      ).isValid();
  }
  /* Run isValid on anything not caught by the rest of the function */
  return !dayjs(source).isValid();
}

// set time to middle of the day to avoid time zone/daylight savings shenanigans
export function addTimeToDateString(date: string | Date) {
  if (!date || isInvalidDateOrString(date)) return null;
  let dateString;
  if (date instanceof Date) {
    dateString = date.toISOString().split("T")?.[0];
  }
  if (typeof date === "string" && date.includes("T")) {
    dateString = date?.split("T")?.[0];
  }
  if (typeof date === "string") {
    // handle 2/5/2024 format from workpack options without regex
    dateString = new Date(date).toISOString().split("T")?.[0];
  }
  dateString += "T12:30:30.0000Z";
  return dateString;
}

export const formatDateString = (source: string | Date | null | undefined, d: I18nDateFormatter) => {
  if (!source || isInvalidDateOrString(source)) {
    return "--";
  }
  const dateString = addTimeToDateString(source);
  if (!dateString) return "--";
  const date = new Date(dateString);
  return d(date, "short");
};

export const formatTimeString = (source: string | Date | null, d: I18nDateFormatter) => {
  if (!source || isInvalidDateOrString(source)) {
    return "--";
  }
  const date = new Date(source as string);
  return d(date, "timeShort");
};

export const isValidDateString = (timestamp: string) => {
  return dayjs(timestamp).isValid();
};

export const getDateStringDay = (timestamp: string) => {
  if (isInvalidDateOrString(timestamp) || timestamp?.length < 3) {
    return "";
  }
  const dateString = addTimeToDateString(timestamp);
  if (dateString === null || dateString === undefined) return 0;
  const date = new Date(dateString!);
  return date.getDay();
};

function padNumber(num: number) {
  if (num < 10) return num.toString().padStart(2, "0");
  return num;
}

export const getDateString = (source: string | Date | null) => {
  if (!source || isInvalidDateOrString(source)) {
    return null;
  }
  const dateString = addTimeToDateString(source);
  if (!dateString) return null;
  const date = new Date(dateString);
  const month = padNumber(date.getMonth() + 1);
  const day = padNumber(date.getDate());
  return `${date.getFullYear()}-${month}-${day}`;
};

export function formatDateLabel(timestamp: string | Date | null | undefined, locale: string) {
  if (!timestamp || isInvalidDateOrString(timestamp)) return null;
  const dateTimeString = addTimeToDateString(timestamp);
  if (!dateTimeString) return null;
  const dateOptions = {
    year: "numeric" as const,
    month: "2-digit" as const,
    day: "2-digit" as const,
  };
  const date = new Date(dateTimeString);
  const dateString = new Intl.DateTimeFormat(locale, dateOptions).format(date);
  const shortWeekday = new Intl.DateTimeFormat(locale, { weekday: "short" }).format(date);

  return `${shortWeekday}, ${dateString}`;
}

export function formatDateLabelName(timestamp: string | Date | null | undefined, locale: string) {
  if (!timestamp || isInvalidDateOrString(timestamp)) return null;
  const dateTimeString = addTimeToDateString(timestamp);
  if (!dateTimeString) return null;
  const date = new Date(dateTimeString);
  const shortWeekday = new Intl.DateTimeFormat(locale, { weekday: "short" }).format(date);

  return `${shortWeekday}`;
}

export function formatWorkpackDayAndDateString(day: number, date: string, locale: string) {
  if (!day || !date || !locale) return "";
  if (day > 7 || day < 1) return "";
  return `D${day}, ${formatDateLabel(date, locale)}`;
}

export function formatTotalMinutes(totalMins: number, t: I18nTranslate) {
  const hours = Math.floor(totalMins / 60);
  const minutes = totalMins % 60;
  const hrs = t("formFields.hrs");
  const mins = t("formFields.mins");
  return hours > 0 ? `${hours}${hrs} ${minutes}${mins}` : `${minutes}${mins}`;
}

export function getTodayDateTimeString(): string {
  return dayjs(undefined).format("YYYY-MM-DDT00:00:00");
}

export function getTomorrowDate() {
  return dayjs().add(1, "day").toDate();
}

/**
 * Get the number of days between 2 dates.
 *
 * @param start
 * @param end
 */
export function getDayDifference(start: Date, end: Date) {
  return Math.round((Number(end) - Number(start)) / TimeInMS.ONE_DAY);
}

export function addDays(start: Date | string, value: number) {
  const date = new Date(start);
  date.setDate(date.getDate() + value);
  return date;
}

export const TimeInMS = {
  ONE_SECOND: 1000,
  FIVE_SECONDS: 5 * 1000,
  TEN_SECONDS: 10 * 1000,
  THIRTY_SECONDS: 30 * 1000,
  ONE_MINUTE: 60 * 1000,
  FIVE_MINUTES: 5 * 60 * 1000,
  ONE_HOUR: 1000 * 60 * 60,
  ONE_DAY: 1000 * 60 * 60 * 24,
};

export function getWeekStartForSpecificDate(specificDate: Date, weekStartDate: Date): Date {
  const weekStartDay = weekStartDate.getDay();
  const specificDay = specificDate.getDay();

  let difference = weekStartDay - specificDay;
  if (difference > 0) {
    difference -= 7;
  }
  return addDays(specificDate, difference);
}

export function getLesserDate(inputDate: Date, secondDate?: Date): Date | null {
  const currentDate = secondDate ?? new Date();
  currentDate.setHours(0, 0, 0, 0); // Normalize to start of the day

  const receivedInputDate = new Date(inputDate);
  receivedInputDate.setHours(0, 0, 0, 0); // Normalize input date

  return receivedInputDate < currentDate ? receivedInputDate : currentDate;
}
